/**
 * @file packages/patterns/src/groups.js
 * @description The seven groups the twenty-six families sort into.
 *
 * ── Why this is not in the registry ──────────────────────────────────
 *
 * It was, and it cost 52kb. A group is a taxonomy over family NAMES — it needs
 * no pattern definition to be true — but sitting in `registry.js` it could not
 * be imported without the seventy-two definitions, their parameters and their
 * hints coming with it. Nothing tree-shakes them out either, because
 * `PATTERN_BY_ID` and `FAMILIES` are computed at module scope.
 *
 * The sidebar needs seven labels. Making the browser download the whole
 * catalogue to render a menu is the kind of cost that is invisible until
 * somebody profiles the bundle, so the split is structural rather than a
 * comment asking people to be careful.
 *
 * `assertGroupsSound` therefore TAKES the family list rather than importing
 * it — which is also what keeps this file free of the dependency it exists to
 * avoid.
 */

/**
 * The seven groups the twenty-six families sort into.
 *
 * ── Why a second level at all ────────────────────────────────────────
 *
 * `family` is the id prefix, so it is decided by naming rather than by
 * meaning: twenty-six of them, thirteen holding a single pattern. That is a
 * useful key and a useless menu — a sidebar with `weigh (1)` and `link (1)`
 * as peers of `select (10)` tells a reader nothing about where to look.
 *
 * A group is the STAGE at which its patterns can run, which is the same
 * ordering the resolution cascade uses. So the menu, the page and the journey
 * all describe the catalogue the same way, and moving a pattern between
 * stages is one edit here rather than three that can disagree.
 *
 * ── One definition, three consumers ──────────────────────────────────
 *
 * The sidebar is a static file, the page is a React screen and the service
 * builds the facets. Each could have kept its own list of groups; this
 * platform has already paid for that mistake twice — two review price tables
 * disagreeing eightfold, and three tool catalogues that were one table. So
 * this is exported from the pure package and imported by all three.
 *
 * `icon` lives here for the same reason. The sidebar requires one on every
 * row at every depth — a row without one is a row users cannot find in the
 * collapsed rail — and a nav that generated its rows from this list but kept
 * its own icon map would break silently the first time a group was added.
 */
export const PATTERN_GROUPS = Object.freeze([
  {
    code: 'read', label: 'Read the idea', icon: 'bookOpen', order: 10,
    blurb: 'The only patterns that touch the text. Everything else reads what these produce.',
    families: ['read', 'describe']
  },
  {
    code: 'classify', label: 'Classify the project', icon: 'tag', order: 20,
    blurb: 'The labels that decide which corpus every later stage reads. Each can abstain.',
    families: ['classify']
  },
  {
    code: 'scope', label: 'Scope the work', icon: 'columns', order: 30,
    blurb: 'What the document covers and what the person is asked. Coverage problems, not ranking problems.',
    families: ['select', 'question', 'answer', 'default', 'relate', 'weigh', 'knowledge', 'understand', 'discover']
  },
  {
    code: 'converse', label: 'Hold the conversation', icon: 'messageSquare', order: 35,
    blurb: 'How the interview is conducted rather than what it is about — what to do with a turn that was not an answer, when to confirm, and how to say it.',
    families: ['dialogue', 'say']
  },
  {
    code: 'solve', label: 'Shape the solution', icon: 'workflow', order: 40,
    blurb: 'Technology, build-or-buy, effort and cost. Choices that constrain each other, so the output is a configuration rather than a ranked list.',
    families: ['stack', 'buildBuy', 'estimate', 'score', 'match', 'link', 'finance']
  },
  {
    code: 'derive', label: 'Derive from the solution', icon: 'gitBranch', order: 50,
    blurb: 'Team, duties, controls and calendars — computed from what was chosen, never predicted from the idea.',
    families: ['people', 'assure', 'operate', 'content', 'plan', 'design']
  },
  {
    code: 'compose', label: 'Write the document', icon: 'fileText', order: 60,
    blurb: 'Turning resolved facts into sections, and refusing any claim the ledger does not support.',
    families: ['generate', 'practice', 'market']
  },
  {
    code: 'platform', label: 'Run the platform', icon: 'gauge', order: 70,
    blurb: 'Review routing, learning, entitlement and abuse — and the people side: profiles, ladders and the ledger. Decisions about the service rather than about a customer project.',
    families: ['review', 'learn', 'integrity', 'commerce', 'profile', 'ladder', 'ledger']
  }
]);

/** family -> group code. The lookup the page and the service both need. */
export const GROUP_BY_FAMILY = Object.freeze(Object.fromEntries(
  PATTERN_GROUPS.flatMap((g) => g.families.map((f) => [f, g.code]))
));

export const groupOf = (family) => GROUP_BY_FAMILY[String(family || '')] || null;

/**
 * Every family in exactly one group, and no group naming a family that does
 * not exist.
 *
 * Takes the families rather than importing them, so this module stays free of
 * the registry. Run by the test suite, because the failure it prevents is
 * silent: a new family with no group simply vanishes
 * from the menu, and nobody notices a pattern they cannot find.
 */
export const assertGroupsSound = (families = []) => {
  const problems = [];
  const declared = new Set(families);
  const seen = new Set();
  for (const g of PATTERN_GROUPS) {
    for (const f of g.families) {
      if (!declared.has(f)) problems.push(`group "${g.code}" names family "${f}", which no pattern uses`);
      if (seen.has(f)) problems.push(`family "${f}" appears in more than one group`);
      seen.add(f);
    }
  }
  for (const f of declared) {
    if (!seen.has(f)) problems.push(`family "${f}" belongs to no group, so it would not appear in the menu`);
  }
  if (problems.length) {
    const e = new Error(`Pattern groups are unsound:\n  - ${problems.join('\n  - ')}`);
    e.code = 'PATTERN.GROUPS_UNSOUND';
    e.problems = problems;
    throw e;
  }
  return true;
};
